/**
 * Stakewise-specific security verification module.
 *
 * @remarks
 * Contains verification methods for Stakewise protocol operations including
 * stake, withdraw, and claim exit queue transactions.
 *
 * @packageDocumentation
 */
import { throwSecurityModuleMismatchError } from "../security-errors";
import { wrapVerifierCall } from "../security-utils";
import { StakewiseOperationsEnum } from "../types";
/**
 * Stakewise-specific security verification module.
 *
 * @remarks
 * Provides verification methods for Stakewise protocol operations on EVM chains.
 * This module is used internally by the main SecurityModule.
 *
 * @internal
 */
export class SecurityModuleStakewise {
    logger;
    getDecodingSdk;
    decodingSDKNotAvailableErrorMessage = "Decoding SDK not available";
    constructor(options) {
        this.logger = options.logger;
        this.getDecodingSdk = options.getDecodingSdk;
    }
    /**
     * Ensures verification result is successful, throws if not.
     * @throws {SecurityModuleParameterMismatchError} if verification fails
     */
    ensureVerification(result, operation) {
        if (!result.ok) {
            throw throwSecurityModuleMismatchError(operation, result.details, result.message);
        }
    }
    /**
     * Verifies a Stakewise stake transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads (`to`, `data`, `value`) do not carry a chain ID. This
     * module does not verify `request.chainId` against the encoded transaction;
     * ensuring the transaction targets the intended network is the application's
     * responsibility (for example via wallet / signing context or upstream policy).
     *
     * @param request - The original stake request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleStakeParameterMismatchError} if verification fails
     *
     * @example
     * ```typescript
     * const result = stakewise.verifyStakeUnsignedTx(
     *   { assets: 1000000000000000000n, userAddress: "0x...", chainId: "eip155:1" },
     *   { to: "0x...", data: "0x...", value: 1000000000000000000n },
     *   { allowedToAddresses: ["0x..."] }
     * );
     * ```
     */
    verifyStakeUnsignedTx(request, encodedTx, options = {}) {
        const sdk = this.getDecodingSdk();
        if (!sdk)
            throw new Error(this.decodingSDKNotAvailableErrorMessage);
        const result = wrapVerifierCall(StakewiseOperationsEnum.STAKE, () => sdk.ethereumDecodingModule.stakewiseVerifyStakeUnsignedTx(request, encodedTx, options), this.logger);
        this.ensureVerification(result, StakewiseOperationsEnum.STAKE);
        return result;
    }
    /**
     * Verifies a Stakewise withdraw transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID. This module does not verify
     * `request.chainId` against the encoded transaction; ensuring the transaction
     * targets the intended network is the application's responsibility.
     *
     * @param request - The original withdraw request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleWithdrawParameterMismatchError} if verification fails
     *
     * @example
     * ```typescript
     * const result = stakewise.verifyWithdrawUnsignedTx(
     *   { amount: 1000000000000000000n, userAddress: "0x...", chainId: "eip155:1" },
     *   { to: "0x...", data: "0x...", value: 0n }
     * );
     * ```
     */
    verifyWithdrawUnsignedTx(request, encodedTx, options = {}) {
        const sdk = this.getDecodingSdk();
        if (!sdk)
            throw new Error(this.decodingSDKNotAvailableErrorMessage);
        const result = wrapVerifierCall(StakewiseOperationsEnum.WITHDRAW, () => sdk.ethereumDecodingModule.stakewiseVerifyWithdrawUnsignedTx(request, encodedTx, options), this.logger);
        this.ensureVerification(result, StakewiseOperationsEnum.WITHDRAW);
        return result;
    }
    /**
     * Verifies a Stakewise claim exit queue transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID. This module does not verify
     * `request.chainId` against the encoded transaction; ensuring the transaction
     * targets the intended network is the application's responsibility.
     *
     * @param request - The original claim request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleClaimExitQueueParameterMismatchError} if verification fails
     *
     * @example
     * ```typescript
     * const result = stakewise.verifyClaimExitQueueUnsignedTx(
     *   {
     *     positions: [{ exitQueueIndex: "0", positionTicket: "123", timestamp: 1234567890 }],
     *     userAddress: "0x...",
     *     chainId: "eip155:1"
     *   },
     *   { to: "0x...", data: "0x...", value: 0n }
     * );
     * ```
     */
    verifyClaimExitQueueUnsignedTx(request, encodedTx, options = {}) {
        const sdk = this.getDecodingSdk();
        if (!sdk)
            throw new Error(this.decodingSDKNotAvailableErrorMessage);
        const result = wrapVerifierCall(StakewiseOperationsEnum.CLAIM_EXIT_QUEUE, () => sdk.ethereumDecodingModule.stakewiseVerifyClaimExitQueueUnsignedTx(request, encodedTx, options), this.logger);
        this.ensureVerification(result, StakewiseOperationsEnum.CLAIM_EXIT_QUEUE);
        return result;
    }
    /**
     * Verifies a generic unsigned transaction against expected values.
     *
     * @remarks
     * Use this method for arbitrary transactions not covered by Stakewise-specific
     * verifiers. Supports custom ABI decoding, selector matching, and address allowlists.
     *
     * Unsigned EVM payloads do not carry a chain ID; this method does not infer or
     * validate network from `encodedTx` alone. The application must ensure the
     * transaction matches the intended chain (e.g. via options and signing context).
     *
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Verification options including ABI, expected values, and allowlists
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleParameterMismatchError} if verification fails (operation = GENERIC)
     *
     * @example
     * ```typescript
     * const result = stakewise.verifyGenericUnsignedTx(
     *   { to: "0x...", data: "0x...", value: 0n },
     *   {
     *     expectedTo: "0x...",
     *     expectedValue: 0n,
     *     allowedToAddresses: ["0x..."],
     *   }
     * );
     * ```
     */
    verifyGenericUnsignedTx(encodedTx, options = {}) {
        const sdk = this.getDecodingSdk();
        if (!sdk)
            throw new Error(this.decodingSDKNotAvailableErrorMessage);
        const result = wrapVerifierCall(StakewiseOperationsEnum.GENERIC, () => sdk.ethereumDecodingModule.verifyGenericUnsignedTx(encodedTx, options), this.logger);
        this.ensureVerification(result, StakewiseOperationsEnum.GENERIC);
        return result;
    }
    /**
     * Routes Stakewise/EVM operations to appropriate verification methods.
     *
     * @remarks
     * Chain ID is not verified against the encoded transaction (see per-operation
     * methods); the application must enforce the intended network.
     *
     * @param input - The verification request
     * @returns IVerificationResult from the appropriate verification method
     */
    routeOperation(input) {
        switch (input.operation) {
            case StakewiseOperationsEnum.STAKE:
                return this.verifyStakeUnsignedTx(input.request, input.encodedTx, input.options);
            case StakewiseOperationsEnum.WITHDRAW:
                return this.verifyWithdrawUnsignedTx(input.request, input.encodedTx, input.options);
            case StakewiseOperationsEnum.CLAIM_EXIT_QUEUE:
                return this.verifyClaimExitQueueUnsignedTx(input.request, input.encodedTx, input.options);
            default:
                return this.verifyGenericUnsignedTx(input.encodedTx, input.options);
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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